import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  LogOut,
  Mail,
  Phone,
  Trash2,
  ArrowLeft,
  UserPlus,
  ShieldCheck,
  Eye,
  EyeOff,
  Pencil,
  Save,
  X as XIcon,
  Copy,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERMISSIONS = [
  { key: "view_contacts", label: "Anfragen ansehen", desc: "Eingegangene Kontaktformulare lesen" },
  { key: "delete_contacts", label: "Anfragen löschen", desc: "Kontaktanfragen entfernen" },
  { key: "manage_admins", label: "Admins verwalten", desc: "Andere Admins anlegen, bearbeiten, löschen" },
  { key: "view_passwords", label: "Passwörter einsehen", desc: "Klartext-Passwörter anderer Admins anzeigen" },
];

const hasPerm = (me, p) => me && Array.isArray(me.permissions) && me.permissions.includes(p);

function formatDetail(d) {
  if (!d) return "Etwas ist schiefgelaufen.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
}

function LoginView({ onLoggedIn }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("Bitte Benutzername und Passwort eingeben.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { username, password });
      toast.success(`Willkommen, ${data.username}.`);
      onLoggedIn(data);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-yx-bg px-5">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md"
      >
        <Link
          to="/"
          data-testid="admin-back-home"
          className="inline-flex items-center gap-2 text-sm text-yx-muted hover:text-yx-primary mb-8"
        >
          <ArrowLeft size={16} /> Zurück zur Website
        </Link>
        <div className="flex items-center gap-3 mb-8">
          <img
            src="/yardtf-logo-transparent.png"
            alt="YardTF"
            className="h-10 w-auto object-contain"
          />
        </div>
        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-yx-secondary mb-3">
          Admin
        </p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter text-yx-primary mb-8 leading-[0.95]">
          Anmelden
        </h1>
        <form
          data-testid="admin-login-form"
          onSubmit={submit}
          className="space-y-5"
        >
          <div className="flex flex-col gap-2">
            <Label htmlFor="a-user">Benutzername</Label>
            <Input
              id="a-user"
              type="text"
              data-testid="admin-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Admin Name"
              className="bg-white"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="a-pass">Passwort</Label>
            <Input
              id="a-pass"
              type="password"
              data-testid="admin-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="bg-white"
              autoComplete="current-password"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-yx-primary px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-yx-secondary disabled:opacity-60"
          >
            {loading && <Loader2 size={18} className="animate-spin" />}
            {loading ? "Wird angemeldet…" : "Anmelden"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}

function Dashboard({ me, onLogout }) {
  const [tab, setTab] = useState("contacts"); // "contacts" | "team"
  const canManageAdmins = hasPerm(me, "manage_admins");
  const canDeleteContacts = hasPerm(me, "delete_contacts");
  const roleLabel = (() => {
    const perms = me.permissions || [];
    if (PERMISSIONS.every((p) => perms.includes(p.key))) return "Owner";
    if (perms.length === 1 && perms[0] === "view_contacts") return "Viewer";
    return "Custom";
  })();
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/contacts`);
      setItems(data);
      if (data.length && !selected) setSelected(data[0]);
    } catch (err) {
      toast.error("Anfragen konnten nicht geladen werden.");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (_e) {
      /* ignore */
    }
    onLogout();
  };

  const remove = async (id) => {
    if (!window.confirm("Diese Anfrage wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/contacts/${id}`);
      const next = items.filter((i) => i.id !== id);
      setItems(next);
      if (selected?.id === id) setSelected(next[0] || null);
      toast.success("Anfrage gelöscht.");
    } catch (err) {
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  const fmtDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("de-DE", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen bg-yx-bg text-yx-text">
      <header className="sticky top-0 z-40 border-b border-yx-border bg-yx-bg/90 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/yardtf-logo-transparent.png"
              alt="YardTF"
              className="h-9 w-auto object-contain"
            />
            <span className="text-xs font-semibold uppercase tracking-[0.28em] text-yx-secondary hidden sm:inline">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span
              data-testid="admin-me-username"
              className="hidden sm:inline text-sm text-yx-muted"
            >
              {me.username}
              <span className="ml-2 px-2 py-0.5 rounded-full bg-yx-surface text-[10px] font-semibold uppercase tracking-wider text-yx-secondary">
                {roleLabel}
              </span>
            </span>
            <Link
              to="/"
              data-testid="admin-open-site"
              className="text-sm text-yx-primary hover:text-yx-secondary"
            >
              Website
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 rounded-full border border-yx-border px-4 py-2 text-sm font-semibold text-yx-primary hover:border-yx-primary transition-colors"
            >
              <LogOut size={14} /> Abmelden
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 sm:px-8 py-10">
        {canManageAdmins && (
          <div
            data-testid="admin-tabs"
            className="mb-8 inline-flex rounded-full border border-yx-border bg-white p-1"
          >
            <button
              data-testid="admin-tab-contacts"
              onClick={() => setTab("contacts")}
              className={`px-5 py-2 text-sm font-semibold rounded-full transition-colors ${
                tab === "contacts"
                  ? "bg-yx-primary text-white"
                  : "text-yx-primary hover:bg-yx-surface"
              }`}
            >
              Anfragen
            </button>
            <button
              data-testid="admin-tab-team"
              onClick={() => setTab("team")}
              className={`px-5 py-2 text-sm font-semibold rounded-full transition-colors ${
                tab === "team"
                  ? "bg-yx-primary text-white"
                  : "text-yx-primary hover:bg-yx-surface"
              }`}
            >
              Team
            </button>
          </div>
        )}

        {tab === "team" && canManageAdmins ? (
          <TeamPanel me={me} />
        ) : (
          <>
        <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-yx-secondary mb-3">
              Eingang
            </p>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-yx-primary leading-[0.95]">
              Anfragen{items ? ` (${items.length})` : ""}
            </h1>
          </div>
          <button
            onClick={load}
            data-testid="admin-refresh"
            className="self-start sm:self-auto rounded-full border border-yx-border px-4 py-2 text-sm font-semibold text-yx-primary hover:border-yx-primary transition-colors"
          >
            Aktualisieren
          </button>
        </div>

        {items === null ? (
          <div className="flex items-center gap-2 text-yx-muted">
            <Loader2 size={16} className="animate-spin" /> Wird geladen…
          </div>
        ) : items.length === 0 ? (
          <div
            data-testid="admin-empty"
            className="rounded-sm border border-dashed border-yx-border bg-white p-10 text-center text-yx-muted"
          >
            Noch keine Anfragen. Sobald jemand das Formular ausfüllt, erscheint es hier.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <ul
              data-testid="admin-list"
              className="lg:col-span-5 rounded-sm border border-yx-border bg-white overflow-hidden divide-y divide-yx-border"
            >
              {items.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => setSelected(c)}
                    data-testid={`admin-item-${c.id}`}
                    className={`w-full text-left px-5 py-4 transition-colors ${
                      selected?.id === c.id
                        ? "bg-yx-surface"
                        : "hover:bg-yx-surface/60"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-semibold text-yx-primary truncate">
                        {c.name}
                      </span>
                      <span className="text-xs text-yx-muted whitespace-nowrap">
                        {fmtDate(c.created_at)}
                      </span>
                    </div>
                    <div className="mt-1 mb-1 flex flex-wrap gap-1.5">
                      {(c.service ? c.service.split(",").map((s) => s.trim()).filter(Boolean) : []).slice(0, 3).map((s, i) => (
                        <span
                          key={i}
                          className="inline-block rounded-full bg-yx-surface px-2 py-0.5 text-[10px] font-medium text-yx-secondary uppercase tracking-wider"
                        >
                          {s}
                        </span>
                      ))}
                      {c.service && c.service.split(",").length > 3 && (
                        <span className="text-[10px] text-yx-muted self-center">
                          +{c.service.split(",").length - 3}
                        </span>
                      )}
                      {!c.service && (
                        <span className="text-[10px] text-yx-muted uppercase tracking-wider">
                          Ohne Leistung
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-yx-text/80 line-clamp-2">
                      {c.message}
                    </p>
                  </button>
                </li>
              ))}
            </ul>

            {selected && (
              <article
                data-testid="admin-detail"
                className="lg:col-span-7 rounded-sm border border-yx-border bg-white p-6 sm:p-8"
              >
                <div className="flex items-start justify-between mb-6 gap-4">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-[0.28em] text-yx-secondary mb-2">
                      Anfrage
                    </p>
                    <h2 className="font-display text-3xl font-bold tracking-tighter text-yx-primary leading-tight">
                      {selected.name}
                    </h2>
                    <p className="text-sm text-yx-muted mt-1">
                      {fmtDate(selected.created_at)}
                    </p>
                    {selected.service && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {selected.service.split(",").map((s) => s.trim()).filter(Boolean).map((s, i) => (
                          <span
                            key={i}
                            data-testid={`admin-detail-service-${i}`}
                            className="inline-block rounded-full border border-yx-border bg-yx-surface px-3 py-1 text-xs font-medium text-yx-primary"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  {canDeleteContacts && (
                    <button
                      onClick={() => remove(selected.id)}
                      data-testid="admin-delete"
                      className="text-yx-muted hover:text-destructive transition-colors p-2 rounded-full hover:bg-yx-surface"
                      aria-label="Löschen"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  {selected.email && (
                    <a
                      href={`mailto:${selected.email}`}
                      className="flex items-center gap-3 rounded-sm border border-yx-border p-4 hover:border-yx-primary transition-colors"
                    >
                      <Mail size={18} className="text-yx-secondary" />
                      <div className="min-w-0">
                        <div className="text-xs text-yx-muted">E-Mail</div>
                        <div className="text-sm truncate">{selected.email}</div>
                      </div>
                    </a>
                  )}
                  {selected.phone && (
                    <a
                      href={`tel:${selected.phone}`}
                      className="flex items-center gap-3 rounded-sm border border-yx-border p-4 hover:border-yx-primary transition-colors"
                    >
                      <Phone size={18} className="text-yx-secondary" />
                      <div className="min-w-0">
                        <div className="text-xs text-yx-muted">Telefon</div>
                        <div className="text-sm truncate">{selected.phone}</div>
                      </div>
                    </a>
                  )}
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-yx-secondary mb-3">
                    Nachricht
                  </p>
                  <div className="rounded-sm border-l-2 border-yx-secondary bg-yx-surface p-5 text-sm leading-relaxed whitespace-pre-wrap">
                    {selected.message}
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  {selected.email ? (
                    <>
                      {(() => {
                        const gmailCompose =
                          "https://mail.google.com/mail/?view=cm&fs=1" +
                          "&authuser=info.yardtf@gmail.com" +
                          `&to=${encodeURIComponent(selected.email)}` +
                          `&su=${encodeURIComponent("Re: Ihre Anfrage bei YardTF Gartenservice")}` +
                          `&body=${encodeURIComponent(
                            `Hallo ${selected.name},\n\nvielen Dank für Ihre Anfrage.\n\n---\nIhre Nachricht vom ${fmtDate(selected.created_at)}:\n${selected.message}\n---\n\nMit freundlichen Grüßen\nYardTF Gartenservice`
                          )}`;
                        return (
                          <a
                            href={gmailCompose}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="admin-reply"
                            className="inline-flex items-center gap-2 rounded-full bg-yx-primary px-6 py-3 text-sm font-semibold text-white hover:bg-yx-secondary transition-colors"
                          >
                            In Gmail antworten
                          </a>
                        );
                      })()}
                      <a
                        href={`mailto:${selected.email}?subject=${encodeURIComponent("Re: Ihre Anfrage bei YardTF Gartenservice")}`}
                        data-testid="admin-reply-mailto"
                        className="inline-flex items-center gap-2 rounded-full border border-yx-border px-6 py-3 text-sm font-semibold text-yx-primary hover:border-yx-primary transition-colors"
                      >
                        Standard-Mail-App
                      </a>
                    </>
                  ) : (
                    selected.phone && (
                      <a
                        href={`tel:${selected.phone}`}
                        data-testid="admin-reply-tel"
                        className="inline-flex items-center gap-2 rounded-full bg-yx-primary px-6 py-3 text-sm font-semibold text-white hover:bg-yx-secondary transition-colors"
                      >
                        Anrufen
                      </a>
                    )
                  )}
                </div>
              </article>
            )}
          </div>
        )}
          </>
        )}
      </main>
    </div>
  );
}

function PermissionCheckboxes({ value, onChange, idPrefix }) {
  const toggle = (key) => {
    const has = value.includes(key);
    onChange(has ? value.filter((k) => k !== key) : [...value, key]);
  };
  return (
    <div className="flex flex-col gap-2" data-testid={`${idPrefix}-perms`}>
      {PERMISSIONS.map((p) => {
        const checked = value.includes(p.key);
        return (
          <label
            key={p.key}
            data-testid={`${idPrefix}-perm-${p.key}`}
            className={`flex items-start gap-3 rounded-sm border p-3 cursor-pointer transition-colors ${
              checked
                ? "border-yx-primary bg-yx-surface"
                : "border-yx-border bg-white hover:border-yx-accent"
            }`}
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={() => toggle(p.key)}
              className="mt-1 h-4 w-4 accent-yx-primary"
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-yx-primary">{p.label}</div>
              <div className="text-xs text-yx-muted">{p.desc}</div>
            </div>
          </label>
        );
      })}
    </div>
  );
}

function TeamPanel({ me }) {
  const meUsername = me.username;
  const canViewPasswords = hasPerm(me, "view_passwords");
  const [admins, setAdmins] = useState(null);
  const [selected, setSelected] = useState(null); // username of admin being edited

  const [createForm, setCreateForm] = useState({
    username: "",
    password: "",
    permissions: ["view_contacts"],
  });
  const [creating, setCreating] = useState(false);

  // Edit state per selection
  const [editPerms, setEditPerms] = useState([]);
  const [editNewPassword, setEditNewPassword] = useState("");
  const [showPwUsername, setShowPwUsername] = useState(null);
  const [revealedPw, setRevealedPw] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admins`);
      setAdmins(data);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Fehler beim Laden.");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startEdit = (a) => {
    setSelected(a.username);
    setEditPerms([...(a.permissions || [])]);
    setEditNewPassword("");
    setShowPwUsername(null);
    setRevealedPw("");
  };

  const cancelEdit = () => {
    setSelected(null);
    setShowPwUsername(null);
    setRevealedPw("");
  };

  const create = async (e) => {
    e.preventDefault();
    if (!createForm.username || !createForm.password) {
      toast.error("Benutzername und Passwort sind erforderlich.");
      return;
    }
    if (createForm.password.length < 6) {
      toast.error("Passwort muss mindestens 6 Zeichen haben.");
      return;
    }
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/admins`, createForm);
      setAdmins((cur) => [...(cur || []), data]);
      setCreateForm({ username: "", password: "", permissions: ["view_contacts"] });
      toast.success(`Admin „${data.username}" angelegt.`);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const remove = async (username) => {
    if (!window.confirm(`Admin „${username}" wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/admins/${username}`);
      setAdmins((cur) => cur.filter((a) => a.username !== username));
      if (selected === username) cancelEdit();
      toast.success(`„${username}" gelöscht.`);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };

  const saveEdit = async () => {
    if (!selected) return;
    const body = { permissions: editPerms };
    if (editNewPassword) {
      if (editNewPassword.length < 6) {
        toast.error("Passwort muss mindestens 6 Zeichen haben.");
        return;
      }
      body.password = editNewPassword;
    }
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/admins/${selected}`, body);
      setAdmins((cur) => cur.map((a) => (a.username === selected ? data : a)));
      setEditNewPassword("");
      if (showPwUsername === selected && editNewPassword) {
        setRevealedPw(editNewPassword);
      }
      toast.success("Änderungen gespeichert.");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const revealPassword = async (username) => {
    try {
      const { data } = await axios.get(`${API}/admins/${username}/password`);
      if (data.password === null) {
        toast.info("Passwort wurde vor Aktivierung dieser Funktion gesetzt — bitte neu vergeben.");
        setRevealedPw("");
      } else {
        setRevealedPw(data.password);
      }
      setShowPwUsername(username);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Passwort kopiert.");
    } catch {
      toast.error("Kopieren fehlgeschlagen.");
    }
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString("de-DE", {
        day: "2-digit", month: "2-digit", year: "numeric",
      });
    } catch {
      return "";
    }
  };

  const permLabels = (perms) => {
    if (PERMISSIONS.every((p) => perms.includes(p.key))) return "Owner · Alle Rechte";
    if (perms.length === 1 && perms[0] === "view_contacts") return "Viewer · Nur ansehen";
    return `Custom · ${perms.length} Rechte`;
  };

  const selectedAdmin = admins?.find((a) => a.username === selected) || null;

  return (
    <div data-testid="team-panel" className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <section className="lg:col-span-7 rounded-sm border border-yx-border bg-white overflow-hidden">
        <div className="px-6 py-5 border-b border-yx-border flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-yx-secondary mb-2">
              Team
            </p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tighter text-yx-primary">
              Admin-Konten{admins ? ` (${admins.length})` : ""}
            </h2>
          </div>
        </div>
        {admins === null ? (
          <div className="p-6 flex items-center gap-2 text-yx-muted">
            <Loader2 size={16} className="animate-spin" /> Wird geladen…
          </div>
        ) : (
          <ul data-testid="team-list" className="divide-y divide-yx-border">
            {admins.map((a) => {
              const isSelf = a.username === meUsername;
              const active = selected === a.username;
              return (
                <li
                  key={a.username}
                  data-testid={`team-row-${a.username}`}
                  className={`px-6 py-4 transition-colors ${
                    active ? "bg-yx-surface" : ""
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <button
                      onClick={() => startEdit(a)}
                      data-testid={`team-edit-${a.username}`}
                      className="flex-1 min-w-0 text-left"
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-yx-primary truncate">
                          {a.username}
                        </span>
                        {isSelf && (
                          <span className="text-[10px] uppercase tracking-wider font-semibold text-yx-secondary">
                            (Sie)
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-xs text-yx-muted">
                        {a.permissions?.includes("manage_admins") ? (
                          <ShieldCheck size={12} className="text-yx-secondary" />
                        ) : (
                          <Eye size={12} className="text-yx-secondary" />
                        )}
                        <span className="uppercase tracking-wider font-semibold">
                          {permLabels(a.permissions || [])}
                        </span>
                        {a.created_at && (
                          <span className="text-yx-muted/70">· seit {fmt(a.created_at)}</span>
                        )}
                      </div>
                    </button>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => startEdit(a)}
                        data-testid={`team-edit-btn-${a.username}`}
                        className="text-yx-muted hover:text-yx-primary transition-colors p-2 rounded-full hover:bg-yx-bg"
                        aria-label="Bearbeiten"
                        title="Bearbeiten"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => remove(a.username)}
                        disabled={isSelf}
                        data-testid={`team-delete-${a.username}`}
                        className="text-yx-muted hover:text-destructive disabled:opacity-30 disabled:cursor-not-allowed transition-colors p-2 rounded-full hover:bg-yx-bg"
                        aria-label="Löschen"
                        title={isSelf ? "Sie können sich nicht selbst löschen" : "Löschen"}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <aside className="lg:col-span-5">
        {selectedAdmin ? (
          <div
            data-testid="team-edit-panel"
            className="rounded-sm border border-yx-border bg-white p-6 space-y-5"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-yx-surface p-2">
                  <Pencil size={16} className="text-yx-secondary" />
                </div>
                <h3 className="font-display text-xl font-bold text-yx-primary">
                  {selectedAdmin.username} bearbeiten
                </h3>
              </div>
              <button
                onClick={cancelEdit}
                data-testid="team-edit-cancel"
                className="text-yx-muted hover:text-yx-primary p-1 rounded-full hover:bg-yx-surface"
                aria-label="Schließen"
              >
                <XIcon size={16} />
              </button>
            </div>

            {/* Password section */}
            <div className="flex flex-col gap-2">
              <Label>Aktuelles Passwort</Label>
              <div className="flex items-center gap-2">
                <div className="flex-1 rounded-sm border border-yx-border bg-yx-surface px-3 py-2 font-mono text-sm text-yx-primary min-h-[38px] flex items-center">
                  {showPwUsername === selectedAdmin.username ? (
                    <span data-testid="team-current-password">
                      {revealedPw || "—"}
                    </span>
                  ) : (
                    <span className="tracking-widest text-yx-muted">••••••••</span>
                  )}
                </div>
                {canViewPasswords ? (
                  showPwUsername === selectedAdmin.username ? (
                    <>
                      <button
                        onClick={() => {
                          setShowPwUsername(null);
                          setRevealedPw("");
                        }}
                        data-testid="team-hide-password"
                        className="p-2 rounded-full text-yx-muted hover:text-yx-primary hover:bg-yx-surface transition-colors"
                        title="Verbergen"
                      >
                        <EyeOff size={16} />
                      </button>
                      {revealedPw && (
                        <button
                          onClick={() => copyToClipboard(revealedPw)}
                          data-testid="team-copy-password"
                          className="p-2 rounded-full text-yx-muted hover:text-yx-primary hover:bg-yx-surface transition-colors"
                          title="Kopieren"
                        >
                          <Copy size={16} />
                        </button>
                      )}
                    </>
                  ) : (
                    <button
                      onClick={() => revealPassword(selectedAdmin.username)}
                      data-testid="team-show-password"
                      className="p-2 rounded-full text-yx-muted hover:text-yx-primary hover:bg-yx-surface transition-colors"
                      title="Anzeigen"
                    >
                      <Eye size={16} />
                    </button>
                  )
                ) : null}
              </div>
              {!canViewPasswords && (
                <p className="text-xs text-yx-muted">
                  Zum Anzeigen wird die Berechtigung „Passwörter einsehen&#34; benötigt.
                </p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="t-newpw">Neues Passwort setzen</Label>
              <Input
                id="t-newpw"
                type="text"
                data-testid="team-new-password"
                value={editNewPassword}
                onChange={(e) => setEditNewPassword(e.target.value)}
                placeholder="Leer lassen = unverändert"
                className="bg-white"
              />
            </div>

            {/* Permission checkboxes */}
            <div className="flex flex-col gap-2">
              <Label>Berechtigungen</Label>
              <PermissionCheckboxes
                value={editPerms}
                onChange={setEditPerms}
                idPrefix="edit"
              />
            </div>

            <button
              onClick={saveEdit}
              disabled={saving}
              data-testid="team-save-edit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-yx-primary px-6 py-3 text-sm font-semibold text-white hover:bg-yx-secondary disabled:opacity-60 transition-colors"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              {saving ? "Wird gespeichert…" : "Speichern"}
            </button>
          </div>
        ) : (
          <form
            onSubmit={create}
            data-testid="team-create-form"
            className="rounded-sm border border-yx-border bg-white p-6 space-y-5"
          >
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-yx-surface p-2">
                <UserPlus size={18} className="text-yx-secondary" />
              </div>
              <h3 className="font-display text-xl font-bold text-yx-primary">
                Admin anlegen
              </h3>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="t-user">Benutzername</Label>
              <Input
                id="t-user"
                data-testid="team-username"
                value={createForm.username}
                onChange={(e) => setCreateForm((f) => ({ ...f, username: e.target.value }))}
                placeholder="Admin Name"
                className="bg-white"
                autoCapitalize="none"
                autoCorrect="off"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="t-pass">Passwort</Label>
              <Input
                id="t-pass"
                type="text"
                data-testid="team-password"
                value={createForm.password}
                onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="Mind. 6 Zeichen"
                className="bg-white"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label>Berechtigungen</Label>
              <PermissionCheckboxes
                value={createForm.permissions}
                onChange={(perms) => setCreateForm((f) => ({ ...f, permissions: perms }))}
                idPrefix="create"
              />
            </div>
            <button
              type="submit"
              disabled={creating}
              data-testid="team-create-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-yx-primary px-6 py-3 text-sm font-semibold text-white hover:bg-yx-secondary disabled:opacity-60 transition-colors"
            >
              {creating && <Loader2 size={16} className="animate-spin" />}
              {creating ? "Wird angelegt…" : "Admin anlegen"}
            </button>
            <p className="text-xs text-yx-muted leading-relaxed">
              Setze die Berechtigungen per Häkchen. Ohne „Anfragen ansehen&#34; erscheinen keine Anfragen im Dashboard.
            </p>
          </form>
        )}
      </aside>
    </div>
  );
}

export default function Admin() {
  const [me, setMe] = useState(null); // null=checking, false=logged out, obj=logged in

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`);
        setMe(data);
      } catch {
        setMe(false);
      }
    })();
  }, []);

  if (me === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-yx-bg">
        <Loader2 className="animate-spin text-yx-secondary" size={28} />
      </div>
    );
  }
  if (!me) return <LoginView onLoggedIn={(u) => setMe(u)} />;
  return <Dashboard me={me} onLogout={() => setMe(false)} />;
}
